import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Steps, Flex, Button } from 'antd';
import { useTripTaskStore } from 'shared/stores';
import { ETripTaskType } from 'types/trips';
import { CreationMode } from './components/creation-mode';
import { TaskDetails } from './components/task-details/task-details';
import { TaskType } from './components/task-type';
import { EStepKey, ELocationMode, type ITripCreateTaskModalProps } from './types';
import { buildTripTaskPayload, validateDetailsStep } from './utils';

export const TripCreateTaskModal = (props: ITripCreateTaskModalProps) => {
  const { open, onCancel, onSubmit, isPending } = props;
  const { t } = useTranslation('trip');
  const [currentIndex, setCurrentIndex] = useState(0);
  const storeState = useTripTaskStore();
  const { taskType, reset, creationMode, cargoTrips, clearCargoTrips } = storeState;

  const selectedCargoIds = cargoTrips.map((cargoTrip) => cargoTrip.id);
  const isMultipleCargos = selectedCargoIds.length > 1;

  const steps = [
    { key: EStepKey.taskType, title: t('create_tasks_step_task_type') },
    ...(isMultipleCargos ? [{ key: EStepKey.creationMode, title: t('create_tasks_step_creation_mode') }] : []),
    { key: EStepKey.details, title: t('create_tasks_step_details') },
  ];

  const currentStepKey = steps[currentIndex]?.key;
  const isFirstStep = currentIndex <= 0;
  const isLastStep = currentIndex === steps.length - 1;

  const validateFormStep = () => {
    switch (currentStepKey) {
      case EStepKey.taskType:
        return Boolean(taskType);
      case EStepKey.details:
        return validateDetailsStep(storeState);
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (
      currentStepKey === EStepKey.creationMode &&
      isMultipleCargos &&
      creationMode === 'separate_tasks' &&
      (taskType === ETripTaskType.loading || taskType === ETripTaskType.unloading)
    ) {
      const payload = buildTripTaskPayload({ ...storeState, locationMode: ELocationMode.fromCargo }, selectedCargoIds);
      onSubmit(payload);
      return;
    }

    if (currentIndex < steps.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleFinish = () => {
    const payload = buildTripTaskPayload(storeState, selectedCargoIds);
    onSubmit(payload);
  };

  useEffect(() => {
    if (open) {
      reset();
      setCurrentIndex(0);
    } else {
      clearCargoTrips();
    }
  }, [open, reset, clearCargoTrips]);

  const renderStepContent = (stepKey: EStepKey) => {
    switch (stepKey) {
      case EStepKey.taskType:
        return <TaskType />;
      case EStepKey.creationMode:
        return <CreationMode />;
      case EStepKey.details:
        return <TaskDetails />;
      default:
        return null;
    }
  };

  const currentStepComponent = currentStepKey ? renderStepContent(currentStepKey) : null;
  const isStepValid = validateFormStep();

  return (
    <Modal
      open={open}
      title={t('create_tasks_modal_title')}
      width={720}
      footer={
        <Flex justify='space-between'>
          <Button onClick={onCancel}>{t('common:cancel')}</Button>
          <Flex gap='small'>
            <Button disabled={isFirstStep} onClick={handlePrev}>
              {t('common:back')}
            </Button>
            {isLastStep ? (
              <Button type='primary' disabled={!isStepValid || isPending} onClick={handleFinish}>
                {t('common:save')}
              </Button>
            ) : (
              <Button type='primary' disabled={!isStepValid || isPending} onClick={handleNext}>
                {t('common:next')}
              </Button>
            )}
          </Flex>
        </Flex>
      }
      onCancel={onCancel}>
      <Flex gap='large' vertical>
        <Steps current={currentIndex} items={steps} />
        {currentStepComponent}
      </Flex>
    </Modal>
  );
};
